import React from "react";

type SpacingValue = number | string;

/**
 * Mantine-style spacing shorthand props supported by primitive components.
 * These are resolved into concrete CSS `margin`/`padding` values.
 */
export type SpacingProps = {
  m?: SpacingValue;
  mt?: SpacingValue;
  mb?: SpacingValue;
  ml?: SpacingValue;
  mr?: SpacingValue;
  mx?: SpacingValue;
  my?: SpacingValue;
  p?: SpacingValue;
  pt?: SpacingValue;
  pb?: SpacingValue;
  pl?: SpacingValue;
  pr?: SpacingValue;
  px?: SpacingValue;
  py?: SpacingValue;
};

/**
 * The `sx` prop accepts a plain style object (optionally including the spacing
 * shorthands) and is resolved into a `React.CSSProperties` object.
 */
export type SxObject = React.CSSProperties & SpacingProps;

/**
 * Props accepted by primitive components for inline styling: an optional `sx`
 * object plus the top-level spacing shorthands.
 */
export type SxProps = {
  sx?: SxObject;
} & SpacingProps;

/**
 * @description All Mantine-style spacing shorthand keys, in margin-then-padding
 * order. Used to detect which props route through the spacing resolver.
 */
const SPACING_KEYS: (keyof SpacingProps)[] = [
  "m", "mt", "mb", "ml", "mr", "mx", "my",
  "p", "pt", "pb", "pl", "pr", "px", "py",
];

/**
 * Converts a Mantine numeric spacing token into a pixel string; strings pass
 * through unchanged so callers can supply explicit units or theme sizes.
 */
function toCssSize(value: SpacingValue): string {
  return typeof value === "number" ? `${value}px` : value;
}

/**
 * Applies a single spacing shorthand onto a CSS style object.
 */
function applySpacing(
  style: React.CSSProperties,
  key: keyof SpacingProps,
  value: SpacingValue
): void {
  const size = toCssSize(value);
  const map: Record<keyof SpacingProps, (keyof React.CSSProperties)[]> = {
    m: ["margin"], mt: ["marginTop"], mb: ["marginBottom"],
    ml: ["marginLeft"], mr: ["marginRight"],
    mx: ["marginLeft", "marginRight"], my: ["marginTop", "marginBottom"],
    p: ["padding"], pt: ["paddingTop"], pb: ["paddingBottom"],
    pl: ["paddingLeft"], pr: ["paddingRight"],
    px: ["paddingLeft", "paddingRight"], py: ["paddingTop", "paddingBottom"],
  };
  for (const cssKey of map[key]) {
    (style as Record<string, string>)[cssKey] = size;
  }
}

/**
 * Copies each entry of an `sx` object onto the result style, routing spacing
 * shorthands through `applySpacing` and passing plain CSS properties verbatim.
 *
 * @param result Target style object mutated in place.
 * @param sx Source `sx` object whose entries are applied.
 */
function applySxEntries(result: React.CSSProperties, sx: SxObject): void {
  for (const key of Object.keys(sx) as (keyof SxObject)[]) {
    if (SPACING_KEYS.includes(key as keyof SpacingProps)) {
      applySpacing(result, key as keyof SpacingProps, sx[key] as SpacingValue);
    } else {
      (result as Record<string, unknown>)[key] = sx[key];
    }
  }
}

/**
 * Applies the top-level spacing shorthand props onto the result style, skipping
 * any shorthand that was not provided.
 *
 * @param result Target style object mutated in place.
 * @param spacing Spacing shorthand props supplied alongside `sx`.
 */
function applySpacingProps(result: React.CSSProperties, spacing: SpacingProps): void {
  for (const key of SPACING_KEYS) {
    const value = spacing[key];
    if (value !== undefined) applySpacing(result, key, value);
  }
}

/**
 * Resolves the `sx` object and spacing shorthands into a single
 * `React.CSSProperties` object, ready to merge into an element `style`.
 *
 * @param source The `sx` object plus any top-level spacing shorthand props.
 * @returns A concrete CSS style object.
 */
export function resolveSx(source: SxProps): React.CSSProperties {
  const { sx, ...spacing } = source;
  const result: React.CSSProperties = {};
  if (sx) applySxEntries(result, sx);
  applySpacingProps(result, spacing as SpacingProps);
  return result;
}
